/**
 * ManageCustomerCasePage
 *
 * Professional case management page for the Customer ownership model.
 * Works for both Customer:Organization and Customer:User contexts.
 *
 * Features:
 *   - Case list with status indicators and metadata
 *   - Create case dialog
 *   - Inline rename
 *   - Archive with confirmation
 *   - Empty state with onboarding prompt
 */

import React, { useState, useContext, useCallback, Fragment } from 'react';
import {
    Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Chip, Tooltip,
    CircularProgress, Alert, Skeleton, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import UserProfileContext from '../context/userprofilecontext.js';
import SnackbarContext from '../context/snackbar-context.js';
import { useGetCustomerCases, useCreateCustomerCase, useUpdateCustomerCase } from '../api/api-customer-cases.js';
import { useArchiveOrgCase, useReactivateOrgCase, useDeleteOrgCase } from '../api/orgcases.js';
import { useConfirmAction } from '../components/ui/confirmactionhandler.js';
import ShowDeletedToggle from '../components/ui/ShowDeletedToggle.js';
import { handleDisplayResponseResults } from '../api/api_util.js';

import { mutate } from 'swr';
import mutateClearCache from '../api/mutateclearcache.js';

import { PageHeader } from '../design-system';
import BillingRequiredDialog from '../components/ui/BillingRequiredDialog';
import { useGetBillingSettings, useCustomerPriceList } from '../api/api-billing-settings.js';
import { formatCurrency } from '../utils/formatters';
import styles from './page-manage-customer-case.module.css';


// ──────────────────────────────────────────────────────────────────────
// Status config
// ──────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    ACTIVE: { label: 'Active', color: 'success' },
    CREATE_PENDING: { label: 'Creating...', color: 'info' },
    ARCHIVE_PENDING: { label: 'Archiving...', color: 'warning' },
    RESTORE_PENDING: { label: 'Restoring...', color: 'info' },
    ARCHIVED: { label: 'Archived', color: 'default' },
    DELETE_PENDING: { label: 'Deleting...', color: 'warning' },
    DELETED: { label: 'Deleted', color: 'error' },
    ERROR: { label: 'Error', color: 'error' },
};

function getStatusConfig(status) {
    return STATUS_CONFIG[status] || STATUS_CONFIG.ACTIVE;
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '';
    }
}


// ──────────────────────────────────────────────────────────────────────
// Create Case Dialog with Billing Check
// ──────────────────────────────────────────────────────────────────────

function CreateCaseDialog({ open, onClose, onCreate, isCreating, customerID, userProfile }) {
    const [caseName, setCaseName] = useState('');
    const [error, setError] = useState(null);
    const [showBillingRequired, setShowBillingRequired] = useState(false);

    // Check if current user is a super admin - they bypass billing checks
    const isSuperAdmin = userProfile?.isSuperAdmin || false;

    // Only fetch billing settings for non-super-admins when dialog is open
    const { data: billingSettings, isLoading: isBillingLoading } = useGetBillingSettings(
        customerID, 
        open && !isSuperAdmin // Only fetch when dialog is open AND user is not super admin
    );

    // Check if billing is set up by checking for payment methods
    // Super admins bypass this check
    const hasBillingSetup = isSuperAdmin || (billingSettings?.paymentMethods?.length > 0);

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            setCaseName('');
            setError(null);
        }
    }, [open]);

    const handleCreate = () => {
        const trimmed = caseName.trim();
        if (!trimmed) {
            setError('Case name is required');
            return;
        }
        if (trimmed.length < 3) {
            setError('Case name must be at least 3 characters');
            return;
        }
        setError(null);

        // Check if billing is set up before creating case (skip for super admins)
        if (!hasBillingSetup) {
            setShowBillingRequired(true);
            return;
        }

        onCreate({ caseName: trimmed });
    };

    const handleClose = () => {
        setCaseName('');
        setError(null);
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isCreating) {
            handleCreate();
        }
    };

    return (
        <Fragment>
            {/* Create Case Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 'var(--radius-md)' },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <div className={styles.dialogHeader}>
                        <FolderOpenIcon color="primary" />
                        <span className={styles.dialogHeaderTitle}>
                            Create New Case
                        </span>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <p className={styles.dialogDesc}>
                        A case represents a matter or project. Each case has its own
                        document storage, analysis workspace, and billing.
                    </p>
                    <TextField
                        autoFocus
                        label="Case Name"
                        value={caseName}
                        onChange={(e) => {
                            setCaseName(e.target.value);
                            if (error) setError(null);
                        }}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        required
                        disabled={isCreating}
                        error={!!error}
                        helperText={error || 'Choose a descriptive name — you can change it later'}
                        placeholder="e.g., Smith vs. Smith — 2026"
                        inputProps={{ maxLength: 100 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={handleClose} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        disabled={isCreating || !caseName.trim()}
                        startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    >
                        {isCreating ? 'Creating...' : 'Create Case'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Billing Required Dialog */}
            <BillingRequiredDialog
                open={showBillingRequired}
                onClose={() => setShowBillingRequired(false)}
            />
        </Fragment>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Case Card
// ──────────────────────────────────────────────────────────────────────

function CaseCard({ caseData, onSelect, onRename, onArchive, onReactivate, onDelete, isUpdating }) {
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState(caseData.caseName || '');

    const statusConfig = getStatusConfig(caseData.status);
    const isActive = !caseData.status || caseData.status === 'ACTIVE';
    const isPending = caseData.status === 'CREATE_PENDING';
    const isArchived = caseData.status === 'ARCHIVED';
    const isDeleted = caseData.status === 'DELETED';
    const isDeletePending = caseData.status === 'DELETE_PENDING';

    const handleSaveRename = () => {
        const trimmed = editName.trim();
        if (trimmed && trimmed !== caseData.caseName) {
            onRename(caseData.caseID, trimmed);
        }
        setEditing(false);
    };

    const handleCancelRename = () => {
        setEditName(caseData.caseName || '');
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSaveRename();
        if (e.key === 'Escape') handleCancelRename();
    };

    const cardClassName = [
        isActive ? styles.caseCard : styles.caseCardInactive,
        isPending ? styles.cardPending : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cardClassName}
            onClick={() => {
                if (isActive && !editing) onSelect(caseData);
            }}
        >
            <div className={styles.caseHeader}>
                <div className={styles.caseName}>
                    {editing ? (
                        <div className={styles.editRow}>
                            <TextField
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                size="small"
                                autoFocus
                                fullWidth
                                disabled={isUpdating}
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ maxLength: 100 }}
                            />
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleSaveRename(); }}
                                disabled={isUpdating}
                                color="primary"
                            >
                                <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleCancelRename(); }}
                                disabled={isUpdating}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    ) : (
                        <div className={styles.caseNameText}>
                            <WorkOutlineIcon
                                sx={{ color: 'var(--text-secondary)', fontSize: 22 }}
                            />
                            <span>
                                {caseData.caseName || caseData.caseID}
                            </span>
                            <Chip
                                label={statusConfig.label}
                                color={statusConfig.color}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: 'var(--text-xs)' }}
                            />
                        </div>
                    )}

                    {!editing && (
                        <div className={styles.caseMeta}>
                            {caseData.createDate && (
                                <div className={styles.metaItem}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'var(--text-disabled)' }} />
                                    <span className={styles.metaText}>
                                        Created {formatDate(caseData.createDate)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {!isDeleted && !isDeletePending && !editing && (
                    <div className={styles.caseActions}>
                        {isActive && (
                            <>
                                <Tooltip title="Rename">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditing(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Archive">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onArchive(caseData);
                                        }}
                                    >
                                        <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        {isArchived && (
                            <Tooltip title="Reactivate">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onReactivate(caseData);
                                    }}
                                >
                                    <UnarchiveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onDelete && (
                            <Tooltip title="Permanently delete">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(caseData);
                                    }}
                                >
                                    <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Empty State
// ──────────────────────────────────────────────────────────────────────

function EmptyState({ onCreate }) {
    return (
        <div className={styles.emptyState}>
            <FolderOpenIcon sx={{ fontSize: 56 }} className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>
                No cases yet
            </h3>
            <p className={styles.emptyStateDesc}>
                Create your first case to start uploading documents and running analysis.
                Each case gets its own workspace with separate storage and billing.
            </p>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreate}
                size="large"
            >
                Create Your First Case
            </Button>
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Loading State
// ──────────────────────────────────────────────────────────────────────

function LoadingSkeleton() {
    return (
        <div className={styles.skeletonList}>
            {[1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonCard}>
                    <Skeleton variant="text" width="40%" height={28} />
                    <Skeleton variant="text" width="25%" height={18} sx={{ mt: 0.5 }} />
                </div>
            ))}
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Main Page Component
// ──────────────────────────────────────────────────────────────────────

export default function ManageCustomerCasePage() {
    const snackbarContext = useContext(SnackbarContext);
    const userProfileContext = useContext(UserProfileContext);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [pollInterval, setPollInterval] = useState(0);

    // Resolve current customer from context
    const currentCustomer = userProfileContext?.userProfile?.currentCustomer;
    const customerID = currentCustomer?.customerID;

    // API hooks
    const { data: cases, error: fetchError, isLoading } = useGetCustomerCases(
        customerID, !!customerID, pollInterval
    );
    const createCase = useCreateCustomerCase();
    const updateCase = useUpdateCustomerCase();
    const archiveOrgCase = useArchiveOrgCase();
    const reactivateOrgCase = useReactivateOrgCase();
    const deleteOrgCase = useDeleteOrgCase();

    // Effective archival + reactivation fees (strategy + overrides) for the
    // archive / reactivate confirmation prompts, so the customer sees the cost
    // before they act. Falls back to generic wording if the price list hasn't
    // loaded or the fee isn't configured.
    const { data: priceListData } = useCustomerPriceList(customerID, !!customerID);
    const feeLabel = (sku) => {
        const item = priceListData?.priceList?.find((i) => i.sku === sku);
        return item != null && item.amount != null ? `$${formatCurrency(item.amount)}` : null;
    };
    const archivalFeeStr = feeLabel('archival_fee');
    const reactivationFeeStr = feeLabel('reactivation_fee');

    const archiveConfirmMessage =
        'Archive this case? Archived cases can be restored later. '
        + `While archived, this case is billed ${archivalFeeStr ? `${archivalFeeStr}/month` : 'a monthly archival fee'}. `
        + `Reactivating it later incurs ${reactivationFeeStr ? `a one-time ${reactivationFeeStr} fee` : 'a one-time reactivation fee'} and resumes normal monthly billing.`;

    const reactivateConfirmMessage =
        'Reactivate this archived case? Its resources will be restored from the last backup (this may take a few minutes). '
        + `Reactivating incurs ${reactivationFeeStr ? `a one-time ${reactivationFeeStr} reactivation fee` : 'a one-time reactivation fee'} and resumes normal monthly billing for this case.`;

    // Get current user info for assigning groups
    const currentUser = userProfileContext?.userProfile?.currentUser;

    // ── Handlers ──

    const handleCreate = useCallback(async (caseData) => {
        setIsCreating(true);
        try {
            const response = await createCase(customerID, caseData);
            handleDisplayResponseResults(
                response,
                snackbarContext,
                (r) => `Case "${r.item.caseName}" creation in progress — we'll notify you when it's ready`,
                (r) => `Case "${r.item.caseName}" could not be created`,
            );
            setCreateDialogOpen(false);
            mutateClearCache();
            // Refresh case management list
            mutate(['CUSTOMER_CASES', customerID]);
            // Refresh case selector dropdown
            mutate('CASES_FOR_CURRENT_CUSTOMER');
            mutate('CURRENTUSER_CONTEXT');

        } catch (error) {
            console.error('Error creating case:', error);
            snackbarContext.openSnackbarError(
                error?.message || 'Failed to create case. Please try again.'
            );
        } finally {
            setIsCreating(false);
        }
    }, [customerID, createCase, snackbarContext]);

    const handleRename = useCallback(async (caseID, newName) => {
        setIsUpdating(true);
        try {
            const response = await updateCase(customerID, {
                caseID,
                caseName: newName,
            });
            handleDisplayResponseResults(
                response,
                snackbarContext,
                () => `Case renamed to "${newName}"`,
                () => `Failed to rename case`,
            );
            mutate(['CUSTOMER_CASES', customerID]);
            // Refresh case selector dropdown
            mutate('CASES_FOR_CURRENT_CUSTOMER');
            mutate('CURRENTUSER_CONTEXT');
        } catch (error) {
            console.error('Error renaming case:', error);
            snackbarContext.openSnackbarError('Failed to rename case');
        } finally {
            setIsUpdating(false);
        }
    }, [customerID, updateCase, snackbarContext]);

    const handleArchive = useCallback(async (caseData) => {
        // Optimistic update — immediately show ARCHIVE_PENDING
        mutate(
            ['CUSTOMER_CASES', customerID],
            (currentData) => {
                if (!currentData) return currentData;
                return currentData.map(c =>
                    c.caseID === caseData.caseID
                        ? { ...c, status: 'ARCHIVE_PENDING' }
                        : c
                );
            },
            { revalidate: false },
        );

        try {
            const response = await archiveOrgCase(
                currentCustomer?.orgID || customerID,
                caseData.caseID
            );
            handleDisplayResponseResults(
                response,
                snackbarContext,
                () => `Initiated archival of case "${caseData.caseName}"`,
                () => `Case "${caseData.caseName}" could not be archived`,
            );
            mutate(['CUSTOMER_CASES', customerID]);
            // Refresh case selector dropdown
            mutate('CASES_FOR_CURRENT_CUSTOMER');
            mutate('CURRENTUSER_CONTEXT');
        } catch (error) {
            console.error('Error archiving case:', error);
            snackbarContext.openSnackbarError('Failed to archive case');
            mutate(['CUSTOMER_CASES', customerID]);
        }
    }, [customerID, currentCustomer, archiveOrgCase, snackbarContext]);

    const { handleConfirmAction: handleConfirmArchive, ConfirmDialogElement: ConfirmArchiveDialog } =
        useConfirmAction(handleArchive, {
            title: 'Archive Case',
            confirmMessage: archiveConfirmMessage,
        });

    const handleReactivate = useCallback(async (caseData) => {
        // Optimistic update — immediately show RESTORE_PENDING
        mutate(
            ['CUSTOMER_CASES', customerID],
            (currentData) => {
                if (!currentData) return currentData;
                return currentData.map(c =>
                    c.caseID === caseData.caseID
                        ? { ...c, status: 'RESTORE_PENDING' }
                        : c
                );
            },
            { revalidate: false },
        );

        try {
            const response = await reactivateOrgCase(
                currentCustomer?.orgID || customerID,
                caseData.caseID
            );
            handleDisplayResponseResults(
                response,
                snackbarContext,
                () => `Initiated reactivation of case "${caseData.caseName}"`,
                () => `Case "${caseData.caseName}" could not be reactivated`,
            );
            mutate(['CUSTOMER_CASES', customerID]);
            mutate('CASES_FOR_CURRENT_CUSTOMER');
            mutate('CURRENTUSER_CONTEXT');
        } catch (error) {
            console.error('Error reactivating case:', error);
            snackbarContext.openSnackbarError('Failed to reactivate case');
            mutate(['CUSTOMER_CASES', customerID]);
        }
    }, [customerID, currentCustomer, reactivateOrgCase, snackbarContext]);

    const { handleConfirmAction: handleConfirmReactivate, ConfirmDialogElement: ConfirmReactivateDialog } =
        useConfirmAction(handleReactivate, {
            title: 'Reactivate Case',
            confirmMessage: reactivateConfirmMessage,
        });

    const handleDelete = useCallback(async (caseData) => {
        // Optimistic update — immediately show DELETE_PENDING
        mutate(
            ['CUSTOMER_CASES', customerID],
            (currentData) => {
                if (!currentData) return currentData;
                return currentData.map(c =>
                    c.caseID === caseData.caseID
                        ? { ...c, status: 'DELETE_PENDING' }
                        : c
                );
            },
            { revalidate: false },
        );

        try {
            const response = await deleteOrgCase(
                currentCustomer?.orgID || customerID,
                caseData.caseID
            );
            handleDisplayResponseResults(
                response,
                snackbarContext,
                () => `Initiated permanent deletion of case "${caseData.caseName}"`,
                () => `Case "${caseData.caseName}" could not be deleted`,
            );
            mutate(['CUSTOMER_CASES', customerID]);
            // Refresh case selector dropdown + current-user context (the deleted
            // case must drop out of any "active case" the user was in)
            mutate('CASES_FOR_CURRENT_CUSTOMER');
            mutate('CURRENTUSER_CONTEXT');
        } catch (error) {
            console.error('Error deleting case:', error);
            snackbarContext.openSnackbarError('Failed to delete case');
            mutate(['CUSTOMER_CASES', customerID]);
        }
    }, [customerID, currentCustomer, deleteOrgCase, snackbarContext]);

    const { handleConfirmAction: handleConfirmDelete, ConfirmDialogElement: ConfirmDeleteDialog } =
        useConfirmAction(handleDelete, {
            title: 'Permanently Delete Case',
            entityNamePrefix: 'You are about to permanently delete the following case:',
            getEntityName: (c) => c?.caseName || c?.caseID,
            confirmMessage:
                'This permanently destroys ALL data for this case (documents, analysis, and case database) and cannot be undone. '
                + 'Any outstanding charges will be finalized. Type "delete" to confirm.',
            confirmKeyword: 'delete',
            proceedButtonLabel: 'Delete Permanently',
        });

    const handleSelectCase = useCallback((caseData) => {
        // Set this case as the active case in the user context
        // and navigate to the dashboard or case workspace
        if (userProfileContext?.setCurrentCase) {
            userProfileContext.setCurrentCase(caseData);
        }
    }, [userProfileContext]);

    // ── Filter cases ──

    // Convert object to array (API returns cases keyed by caseID)
    const casesArray = cases ? Object.values(cases) : [];

    // Sort by create date, newest first
    casesArray.sort((a, b) => {
        const dateA = a.createDate ? new Date(a.createDate) : 0;
        const dateB = b.createDate ? new Date(b.createDate) : 0;
        return dateB - dateA;
    });


    const hasPendingCases = casesArray.some(
        (c) => c.status === 'CREATE_PENDING' || c.status === 'ARCHIVE_PENDING' || c.status === 'RESTORE_PENDING'
    );

    React.useEffect(() => {
        setPollInterval(hasPendingCases ? 5000 : 0);
    }, [hasPendingCases]);

    // Hide permanently-deleted (tombstoned) cases unless the user opts to show
    // them. They remain in the data for billing/cost reconciliation.
    const filteredCases = casesArray.filter((c) => {
        if (c.status === 'DELETED' && !showDeleted) return false;
        if (!searchFilter) return true;
        const term = searchFilter.toLowerCase();
        return (
            (c.caseName || '').toLowerCase().includes(term) ||
            (c.caseID || '').toLowerCase().includes(term)
        );
    });

    // ── Guard: no customer selected ──

    if (!customerID) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="info">
                    Please select a Customer from the context menu to manage cases.
                </Alert>
            </div>
        );
    }

    // ── Guard: not authorized to manage cases for this Customer ──
    if (!userProfileContext?.userProfile?.isCustomerAdmin()) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="warning">
                    You do not have permission to manage cases for this Customer.
                    Contact your organization administrator for access.
                </Alert>
            </div>
        );
    }

    // ── Render ──

    return (
        <Fragment>
            <div className={styles.page}>
                {/* Header */}
                <PageHeader
                    title="Cases"
                    subtitle={currentCustomer?.orgName || currentCustomer?.displayName || 'Your cases'}
                    actions={
                        casesArray.length > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setCreateDialogOpen(true)}
                            >
                                New Case
                            </Button>
                        )
                    }
                />

                {/* Search (only show when there are cases) */}
                {casesArray.length > 3 && (
                    <TextField
                        placeholder="Search cases..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 'var(--space-2)' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'var(--text-disabled)' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {/* Show-deleted toggle — always available so it's discoverable;
                    permanently-deleted cases are hidden by default. */}
                {casesArray.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <ShowDeletedToggle checked={showDeleted} onChange={setShowDeleted} />
                    </div>
                )}

                {/* Error */}
                {fetchError && (
                    <Alert severity="error" sx={{ mb: 'var(--space-4)' }}>
                        Failed to load cases. Please try refreshing the page.
                    </Alert>
                )}

                {/* Content */}
                {isLoading ? (
                    <LoadingSkeleton />
                ) : casesArray.length === 0 ? (
                    <EmptyState onCreate={() => setCreateDialogOpen(true)} />
                ) : filteredCases.length === 0 ? (
                    <div className={styles.noResults}>
                        No cases match "{searchFilter}"
                    </div>
                ) : (
                    <div className={styles.skeletonList}>
                        {filteredCases.map((caseData) => (
                            <CaseCard
                                key={caseData.caseID}
                                caseData={caseData}
                                onSelect={handleSelectCase}
                                onRename={handleRename}
                                onArchive={handleConfirmArchive}
                                onReactivate={handleConfirmReactivate}
                                onDelete={handleConfirmDelete}
                                isUpdating={isUpdating}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Dialog */}
            <CreateCaseDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCreate={handleCreate}
                isCreating={isCreating}
                customerID={customerID}
                userProfile={userProfileContext?.userProfile}
            />

            {/* Archive Confirmation Dialog */}
            <ConfirmArchiveDialog />

            {/* Reactivate Confirmation Dialog */}
            <ConfirmReactivateDialog />

            {/* Permanent Delete Confirmation Dialog (type "delete" to confirm) */}
            <ConfirmDeleteDialog />
        </Fragment>
    );
}
